/* eslint-disable react-refresh/only-export-components */
import { Children, type ComponentProps, type FC, type ReactNode, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  alpha,
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import Markdown from 'markdown-to-jsx'
import { CodeBlock } from './CodeBlock'

type MarkdownOverrides = NonNullable<ComponentProps<typeof Markdown>['options']>

interface InlineCodeComponentProps {
  readonly children: ReactNode
  readonly theme: Theme
}

interface CodeRendererProps {
  readonly children: ReactNode
  readonly className?: string
  readonly theme: Theme
}

interface TableComponentProps {
  readonly children: ReactNode
  readonly theme: Theme
}

const ExternalLink: FC<ComponentProps<typeof Link>> = ({ href, children, ...rest }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" {...rest}>
    {children}
  </Link>
)

export function useMarkdownOverrides(): MarkdownOverrides {
  const theme = useTheme()

  return useMemo<MarkdownOverrides>(
    () => ({
      overrides: {
        a: {
          component: ExternalLink,
        },
        code: {
          component: CodeRenderer,
          props: { theme },
        },
        h1: {
          component: Typography,
          props: {
            variant: 'h4',
            gutterBottom: true,
            sx: h1Sx,
          },
        },
        h2: {
          component: Typography,
          props: {
            variant: 'h5',
            gutterBottom: true,
            sx: h2Sx,
          },
        },
        h3: {
          component: Typography,
          props: {
            variant: 'h6',
            gutterBottom: true,
            sx: h3Sx,
          },
        },
        p: {
          component: Typography,
          props: {
            variant: 'body1',
            paragraph: true,
            sx: paragraphSx,
          },
        },
        blockquote: {
          component: Box,
          props: {
            component: 'blockquote',
            sx: blockquoteSx(theme),
          },
        },
        table: {
          component: TableComponent,
          props: { theme },
        },
        thead: {
          component: TableHead,
        },
        tbody: {
          component: TableBody,
        },
        tr: {
          component: TableRow,
          props: {
            sx: tableRowSx(theme),
          },
        },
        th: {
          component: TableCell,
          props: {
            component: 'th',
            variant: 'head',
            sx: tableHeaderCellSx(theme),
          },
        },
        td: {
          component: TableCell,
          props: {
            sx: tableCellSx,
          },
        },
      },
    }),
    [theme],
  )
}

function isPath(text: string): boolean {
  return (
    text.startsWith('/') ||
    text.startsWith('./') ||
    text.startsWith('../') ||
    text.startsWith('~') ||
    text.startsWith('\\') ||
    text.includes(':/') ||
    (text.match(/[/\\]/g) || []).length >= 2
  )
}

function isFile(text: string): boolean {
  return /^[^/\\]+\.([a-zA-Z0-9]{1,8})$/.test(text)
}

const CodeRenderer: FC<CodeRendererProps> = ({ children, className, theme }) => {
  const isCodeBlock =
    className ||
    (typeof children === 'string' && children.includes('\n')) ||
    Children.count(children) > 1

  let language: string | undefined
  if (className?.startsWith('lang-')) {
    language = className.replace('lang-', '')
  } else if (className?.startsWith('language-')) {
    language = className.replace('language-', '')
  }

  return isCodeBlock ? (
    <CodeBlock theme={theme} language={language}>
      {children}
    </CodeBlock>
  ) : (
    <InlineCodeRenderer theme={theme}>{children}</InlineCodeRenderer>
  )
}

const InlineCodeRenderer: FC<InlineCodeComponentProps> = ({ children, theme }) => {
  const text = typeof children === 'string' ? children : ''

  if (isPath(text)) {
    return <InlinePath theme={theme}>{children}</InlinePath>
  }

  if (isFile(text)) {
    return <InlineFile theme={theme}>{children}</InlineFile>
  }

  return <InlineCode theme={theme}>{children}</InlineCode>
}

const InlinePath: FC<InlineCodeComponentProps> = ({ children, theme }) => (
  <Typography component="code" sx={inlinePathSx(theme)}>
    {children}
  </Typography>
)

const InlineFile: FC<InlineCodeComponentProps> = ({ children, theme }) => (
  <Typography component="code" sx={inlineFileSx(theme)}>
    {children}
  </Typography>
)

const InlineCode: FC<InlineCodeComponentProps> = ({ children, theme }) => (
  <Typography component="code" sx={inlineCodeSx(theme)}>
    {children}
  </Typography>
)

const TableComponent: FC<TableComponentProps> = ({ children, theme }) => (
  <TableContainer component={Paper} variant="outlined" sx={tableContainerSx(theme)}>
    <Table size="small">{children}</Table>
  </TableContainer>
)

const h1Sx: SxProps<Theme> = {
  fontSize: 'inherit',
  fontWeight: 600,
  mt: 2.5,
  mb: 1.5,
}

const h2Sx: SxProps<Theme> = {
  fontSize: 'inherit',
  fontWeight: 600,
  mt: 2,
  mb: 1.25,
}

const h3Sx: SxProps<Theme> = {
  fontSize: 'inherit',
  fontWeight: 600,
  mt: 1.5,
  mb: 1,
}

const paragraphSx: SxProps<Theme> = {
  fontSize: 'inherit',
  mb: 1.5,
}

const blockquoteSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    my: 2,
    pl: 3,
    py: 1,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: alpha(overlayColor, isDark ? 0.04 : 0.03),
    borderRadius: 1,
    fontStyle: 'italic',
    fontSize: 'inherit',
    '& > *:last-child': { mb: 0 },
  }
}

const tableContainerSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    my: 2,
    borderRadius: 2,
    border: `1px solid ${alpha(overlayColor, isDark ? 0.1 : 0.08)}`,
    boxShadow: `0 2px 8px ${alpha(isDark ? theme.palette.common.black : '#3E2723', isDark ? 0.3 : 0.08)}`,
  }
}

const tableRowSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    '&:nth-of-type(odd)': {
      backgroundColor: alpha(overlayColor, isDark ? 0.03 : 0.02),
    },
    '&:hover': {
      backgroundColor: alpha(overlayColor, isDark ? 0.05 : 0.04),
    },
  }
}

const tableHeaderCellSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    fontWeight: 600,
    fontSize: 'inherit',
    backgroundColor: alpha(overlayColor, isDark ? 0.05 : 0.04),
  }
}

const tableCellSx: SxProps<Theme> = {
  fontSize: 'inherit',
  '& p': { mb: 0 },
}

const inlinePathSx = (theme: Theme): SxProps<Theme> => ({
  fontFamily: 'monospace',
  fontSize: '1.05em',
  color: theme.palette.mode === 'dark' ? '#4fc3f7' : theme.palette.info.dark,
})

const inlineFileSx = (theme: Theme): SxProps<Theme> => ({
  fontFamily: 'monospace',
  fontSize: '1.05em',
  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
  fontWeight: 600,
})

const inlineCodeSx = (theme: Theme): SxProps<Theme> => ({
  fontFamily: 'monospace',
  fontSize: '1.05em',
  color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
})
